import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// ─── Icon helper ────────────────────────────────────────────

function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Component ──────────────────────────────────────────────

export default function NotificarCliente() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const { id } = router.query;
  const [cliente, setCliente] = useState<any>(null);
  const [mensaje, setMensaje] = useState('');
  const [asunto, setAsunto] = useState('Recordatorio: Subida de Documentos');
  const [sending, setSending] = useState(false);
  const [templateType, setTemplateType] = useState<'reminder' | 'urgent' | 'custom'>('reminder');

  const loadCliente = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('*, profiles(*)')
        .eq('id', id)
        .single();

      if (error) throw error;
      setCliente(data);
      
      // Load last upload date
      const { data: lastDoc } = await supabase
        .from('documentos')
        .select('fecha_subida')
        .eq('cliente_id', id)
        .order('fecha_subida', { ascending: false })
        .limit(1)
        .single();

      if (lastDoc) {
        const daysSince = Math.floor(
          (Date.now() - new Date(lastDoc.fecha_subida).getTime()) / (1000 * 60 * 60 * 24)
        );
        
        if (templateType === 'reminder') {
          setMensaje(
            `Estimado/a ${data.persona_contacto},\n\n` +
            `Hemos notado que hace ${daysSince} días que no sube documentos al sistema. ` +
            `Le recordamos la importancia de mantener la documentación actualizada para ` +
            `garantizar el correcto procesamiento de su contabilidad.\n\n` +
            `Si tiene alguna duda o necesita asistencia, no dude en contactarnos.\n\n` +
            `Saludos cordiales,\n${user?.nombre}`
          );
        }
      }
    } catch (error) {
      console.error('Error loading cliente:', error);
      toast.error('Error al cargar cliente');
    }
  }, [id, templateType, user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (id && user) {
      loadCliente();
    }
  }, [id, user, loading, router, loadCliente]);

  const handleTemplateChange = (type: 'reminder' | 'urgent' | 'custom') => {
    setTemplateType(type);
    
    if (!cliente) return;

    if (type === 'reminder') {
      setAsunto('Recordatorio: Subida de Documentos');
      setMensaje(
        `Estimado/a ${cliente.persona_contacto},\n\n` +
        `Le recordamos que es importante mantener sus documentos actualizados en el sistema. ` +
        `Esto nos permite ofrecerle un mejor servicio y mantener su contabilidad al día.\n\n` +
        `Por favor, suba los documentos pendientes cuando le sea posible.\n\n` +
        `Saludos,\n${user?.nombre}`
      );
    } else if (type === 'urgent') {
      setAsunto('URGENTE: Documentos Pendientes - Acción Requerida');
      setMensaje(
        `Estimado/a ${cliente.persona_contacto},\n\n` +
        `Le contactamos con carácter URGENTE. Hemos detectado que lleva un tiempo considerable ` +
        `sin subir documentos al sistema, lo que está afectando el procesamiento de su contabilidad.\n\n` +
        `Es imprescindible que suba los documentos pendientes lo antes posible para evitar retrasos ` +
        `en sus obligaciones fiscales.\n\n` +
        `Por favor, contacte con nosotros si necesita ayuda.\n\n` +
        `Urgentemente,\n${user?.nombre}`
      );
    } else {
      setMensaje('');
    }
  };

  const handleSend = async () => {
    if (!mensaje.trim()) {
      toast.error('Por favor, escribe un mensaje');
      return;
    }

    setSending(true);

    try {
      // Send notification via API
      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clienteId: id,
          asunto,
          mensaje,
          asesorId: user?.id,
          type: 'upload_reminder'
        })
      });

      if (!response.ok) throw new Error('Error sending notification');

      toast.success('Notificación enviada correctamente');
      
      // Log activity
      await supabase.from('actividad_reciente').insert({
        tipo: 'notificacion_cliente',
        descripcion: `Notificación enviada a ${cliente.razon_social}`,
        usuario_id: user?.id,
        usuario_nombre: user?.nombre || 'Asesor',
        enlace: `/asesor/clientes`
      });

      router.push('/asesor/index');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar notificación');
    } finally {
      setSending(false);
    }
  };

  const handleScheduleAutomatic = async () => {
    try {
      const response = await fetch('/api/notifications/schedule-automatic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clienteId: id,
          threshold: 15, // days without upload
          enabled: true
        })
      });

      if (!response.ok) throw new Error('Error scheduling');

      toast.success('Notificaciones automáticas configuradas');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al configurar notificaciones automáticas');
    }
  };

  if (loading || !cliente) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pb-12 pt-8 space-y-12">
          <div className="space-y-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-32 rounded-huge" />
          <Skeleton className="h-24 rounded-huge" />
          <Skeleton className="h-64 rounded-huge" />
        </div>
      </Layout>
    );
  }

  const TEMPLATES = [
    {
      type: 'reminder' as const,
      icon: 'edit_note',
      label: 'Recordatorio',
      desc: 'Mensaje amigable',
    },
    {
      type: 'urgent' as const,
      icon: 'priority_high',
      label: 'Urgente',
      desc: 'Requiere acción',
    },
    {
      type: 'custom' as const,
      icon: 'edit',
      label: 'Personalizado',
      desc: 'Escribe tu mensaje',
    },
  ];

  return (
    <>
      <Head>
        <title>Notificar Cliente — Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto scrollbar-thin">
          <div className="px-12 pb-12 space-y-12">

            {/* ──────── Header ──────── */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Comunicación</p>
                <h1 className="text-5xl font-display font-light text-calm-text">Notificar Cliente</h1>
                <p className="text-calm-muted font-light mt-2">
                  Enviar recordatorio a {cliente.razon_social}
                </p>
              </div>
              <button
                onClick={() => router.push('/asesor/index')}
                className="border border-calm-border rounded-huge w-10 h-10 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all"
                title="Volver"
              >
                <Icon name="arrow_back" className="text-lg" />
              </button>
            </div>

            {/* ──────── Client Info Card ──────── */}
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-500">
                    <Icon name="apartment" className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-display font-light text-calm-text">
                      {cliente.nombre_comercial || cliente.razon_social}
                    </h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm font-light text-calm-muted font-mono">{cliente.nif}</span>
                      <span className="text-sm font-light text-calm-accent">Contacto: {cliente.persona_contacto}</span>
                      <span className="text-sm font-light text-calm-accent">{cliente.email}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleScheduleAutomatic}
                  className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2"
                >
                  <Icon name="schedule" className="text-base" />
                  Configurar Automático
                </button>
              </div>
            </div>

            {/* ──────── Template Selection ──────── */}
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="draft" className="text-fresh-sky-500 text-lg" />
                <h3 className="text-sm font-display font-light text-calm-text">Plantilla de Mensaje</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {TEMPLATES.map((tmpl) => (
                  <button
                    key={tmpl.type}
                    onClick={() => handleTemplateChange(tmpl.type)}
                    className={cn(
                      'p-5 rounded-xl border transition-all text-left group',
                      templateType === tmpl.type
                        ? 'border-calm-text bg-calm-bg'
                        : 'border-calm-border hover:border-calm-text/30'
                    )}
                  >
                    <div className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center mb-3',
                      templateType === tmpl.type
                        ? 'bg-calm-text text-white'
                        : 'bg-fresh-sky-50 text-fresh-sky-500 group-hover:text-calm-text'
                    )}>
                      <Icon name={tmpl.icon} className="text-xl" />
                    </div>
                    <div className="text-sm font-light text-calm-text">{tmpl.label}</div>
                    <div className="text-xs font-light text-calm-accent mt-0.5">{tmpl.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* ──────── Message Form ──────── */}
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="mail" className="text-fresh-sky-500 text-lg" />
                <h3 className="text-sm font-display font-light text-calm-text">Mensaje</h3>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">Asunto</Label>
                <Input
                  id="asunto"
                  type="text"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">Mensaje</Label>
                <Textarea
                  id="mensaje"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={10}
                  className="border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                  placeholder="Escribe tu mensaje aquí…"
                />
              </div>
            </div>

            {/* ──────── Actions ──────── */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleSend}
                disabled={sending || !mensaje.trim()}
                className="flex-1 border border-calm-text bg-calm-text text-white rounded-huge px-5 py-3 text-sm font-light hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <Icon name="send" className="text-base" />
                {sending ? 'Enviando…' : 'Enviar Notificación'}
              </button>
              <button
                onClick={() => router.push('/asesor/index')}
                className="border border-calm-border rounded-huge px-6 py-3 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all"
              >
                Cancelar
              </button>
            </div>

          </div>
        </div>
      </Layout>
    </>
  );
}
